import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Menu, MenuItem, Popover, Stack, Typography } from '@mui/material';
import { addDays, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, startOfMonth, startOfWeek } from 'date-fns';
import { dayKey } from './calendarEvents';
import OrderEventChip from './OrderEventChip';

const MAX_CHIPS = 3;

/**
 * Props: date (any day of the month), buckets ({ 'yyyy-MM-dd': [event] }), notesSummary, availabilityDates
 * (array of 'yyyy-MM-dd' the provider IS available), locale (date-fns), now, onOpenOrder(order),
 * onPickDay(date) (open Agenda on that day), onToggleAvailability(dayKey, nowAvailable),
 * canEditAvailability (false = the context-menu item upsells instead; availability writes are Pro).
 */
export default function MonthView({
  date, buckets, notesSummary, availabilityDates, locale, now, onOpenOrder, onPickDay, onToggleAvailability, canEditAvailability = true,
}) {
  const { t } = useTranslation();
  const [overflow, setOverflow] = useState(null); // { anchor, key }
  const [menu, setMenu] = useState(null); // { x, y, key, available }
  const gridStart = startOfWeek(startOfMonth(date), { locale });
  const gridEnd = endOfWeek(endOfMonth(date), { locale });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const weekdays = Array.from({ length: 7 }, (_, i) => format(addDays(gridStart, i), 'EEEEEE', { locale }));
  const available = new Set(availabilityDates || []);

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
        {weekdays.map((w) => (
          <Typography key={w} variant="caption" color="text.secondary" align="center" sx={{ fontWeight: 600 }}>{w}</Typography>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {days.map((day) => {
          const key = dayKey(day);
          const events = buckets[key] || [];
          const isAvailable = available.has(key);
          const inMonth = isSameMonth(day, date);
          const isToday = isSameDay(day, now);
          return (
            <Box
              key={key}
              data-testid={`cal-day-${key}`}
              data-available={isAvailable ? 'true' : 'false'}
              onContextMenu={(e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, key, available: isAvailable }); }}
              sx={{
                minHeight: 96, p: 0.5, border: 1, borderRadius: 1,
                borderColor: isToday ? 'primary.main' : 'divider',
                bgcolor: !inMonth ? 'action.hover' : isAvailable ? 'background.paper' : 'action.disabledBackground',
                opacity: inMonth ? 1 : 0.6,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="caption" component="button" onClick={() => onPickDay(day)}
                  sx={{ border: 0, background: 'none', cursor: 'pointer', fontWeight: isToday ? 700 : 400, color: isToday ? 'primary.main' : 'text.primary', p: 0 }}
                >
                  {format(day, 'd')}
                </Typography>
                {!isAvailable && inMonth && (
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>{t('orders.calendar.unavailableDay')}</Typography>
                )}
              </Stack>
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {events.slice(0, MAX_CHIPS).map((ev) => (
                  <OrderEventChip key={ev.id} event={ev} note={notesSummary[ev.id]} compact onClick={() => onOpenOrder(ev.order)} />
                ))}
                {events.length > MAX_CHIPS && (
                  <Typography
                    variant="caption" color="primary" component="button"
                    onClick={(e) => setOverflow({ anchor: e.currentTarget, key })}
                    sx={{ border: 0, background: 'none', cursor: 'pointer', textAlign: 'start', p: 0 }}
                  >
                    {t('orders.calendar.more', { count: events.length - MAX_CHIPS })}
                  </Typography>
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>

      <Popover open={Boolean(overflow)} anchorEl={overflow?.anchor} onClose={() => setOverflow(null)}>
        <Stack spacing={0.5} sx={{ p: 1, minWidth: 240 }}>
          {(overflow ? buckets[overflow.key] || [] : []).map((ev) => (
            <OrderEventChip key={ev.id} event={ev} note={notesSummary[ev.id]} onClick={() => { setOverflow(null); onOpenOrder(ev.order); }} />
          ))}
        </Stack>
      </Popover>

      <Menu
        open={Boolean(menu)} onClose={() => setMenu(null)} anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
      >
        <MenuItem onClick={() => { onToggleAvailability(menu.key, menu.available); setMenu(null); }}>
          {menu?.available ? t('orders.calendar.markUnavailable') : t('orders.calendar.markAvailable')}
          {!canEditAvailability && <Typography component="span" variant="caption" color="primary" sx={{ ml: 1 }}>PRO</Typography>}
        </MenuItem>
      </Menu>
    </Box>
  );
}
